import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { getBlob } from "../../offline/opfsBlobCache";
import { subscribeUpload, type UploadRecord } from "../../offline/uploadQueue";

export interface DerivativeInfo {
  kind: string;
  url: string;
  meta: Record<string, unknown>;
}
export interface AssetState {
  upload: (UploadRecord & { progress?: number }) | null;
  derivatives: DerivativeInfo[];
  stored: boolean;
  localUrl: string | null;
}

/** Tracks upload progress, server-side availability and derivatives of an asset hash (spec §8.10 step 6). */
export function useAsset(hash: string | null): AssetState {
  const [upload, setUpload] = useState<AssetState["upload"]>(null);
  const [derivatives, setDerivatives] = useState<DerivativeInfo[]>([]);
  const [stored, setStored] = useState(false);
  const [localUrl, setLocalUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hash) return;
    let url: string | null = null;
    void getBlob(hash).then((b) => {
      if (b) {
        url = URL.createObjectURL(b);
        setLocalUrl(url);
      }
    });
    const off = subscribeUpload(hash, setUpload);
    return () => {
      off();
      if (url) URL.revokeObjectURL(url);
    };
  }, [hash]);

  useEffect(() => {
    if (!hash) return;
    let stop = false;
    let attempts = 0;
    const poll = async () => {
      const res = await api.GET("/api/assets/{hash_hex}/meta", { params: { path: { hash_hex: hash } } }).catch(() => null);
      if (stop) return;
      if (res?.response.ok && res.data) {
        const d = res.data as { state: string; derivatives: DerivativeInfo[]; mime: string };
        setStored(d.state === "stored");
        setDerivatives(d.derivatives);
        const expected = d.mime.startsWith("image/") ? 2 : d.mime.startsWith("video/") || d.mime.startsWith("audio/") ? 1 : 0;
        if (d.state === "stored" && d.derivatives.length >= expected) return;
      }
      if (++attempts < 30) setTimeout(poll, Math.min(15000, 1000 * attempts));
    };
    void poll();
    return () => {
      stop = true;
    };
  }, [hash, upload?.state]);

  return { upload, derivatives, stored, localUrl };
}

export function derivativeUrl(d: DerivativeInfo[], kind: string): string | null {
  return d.find((x) => x.kind === kind)?.url ?? null;
}
